import type { Survey } from '@/types';

export const sampleOnboardingSurvey: Survey = {
  surveyId: 'SVY-five_onboarding_v1',
  title: 'Five AI — New User Onboarding',
  description: 'Personalize your career plan in a few quick steps.',
  type: 'ONBOARDING',
  category: 'USER_ONBOARDING',
  status: 'PUBLISHED',
  configuration: {
    allowBackNavigation: true,
    randomizeQuestions: false,
    randomizeOptions: false,
    timeLimit: 1200,
    showProgressIndicator: true,
    autoSave: true,
    allowMultipleResponses: false,
  },
  questions: [
    {
      id: 'role_title',
      text: 'Role (type your job title)',
      type: 'TEXT_SHORT',
      required: true,
      order: 1,
      heading: {
        id: 'role_heading',
        style: 'heading',
        heading: "What's your current role?",
      },
      section: {
        id: 'RoleScreen',
        heading: 'Role Information',
      },
      validationRules: {
        maxLength: 120,
      },
    },
    {
      id: 'years_experience',
      text: 'Years of experience',
      type: 'SINGLE_CHOICE',
      required: true,
      order: 2,
      section: {
        id: 'RoleScreen',
        heading: 'Role Information',
      },
      options: [
        {
          id: 'exp_0_4',
          text: 'Just Starting (0–4 years)',
          order: 1,
          value: '0-4',
        },
        {
          id: 'exp_5_9',
          text: 'Growing (5–9 years)',
          order: 2,
          value: '5-9',
        },
        {
          id: 'exp_10_19',
          text: 'Experienced (10–19 years)',
          order: 3,
          value: '10-19',
        },
        {
          id: 'exp_20_plus',
          text: 'Veteran (20+ years)',
          order: 4,
          value: '20+',
        },
      ],
    },
    {
      id: 'pain_points',
      text: 'Select up to 2 pain areas',
      type: 'MULTIPLE_CHOICE',
      required: true,
      order: 3,
      heading: {
        id: 'pain_heading',
        style: 'heading',
        heading: "What's keeping you stuck?",
        shortHeading: 'Choose up to 2. You can change later.',
      },
      section: {
        id: 'PainSelectScreen',
        heading: 'Pain Points',
      },
      options: [
        {
          id: 'pain_ai_fast',
          text: 'I feel left behind as AI moves so fast',
          order: 1,
          value: 'AI_FAST',
        },
        {
          id: 'pain_credit',
          text: "I'm not getting credit for my work",
          order: 2,
          value: 'CREDIT',
        },
        {
          id: 'pain_connections',
          text: 'I lack the right connections and support',
          order: 3,
          value: 'CONNECTIONS',
        },
        {
          id: 'pain_confidence',
          text: "I'm not confident speaking up in meetings",
          order: 4,
          value: 'CONFIDENCE',
        },
        {
          id: 'pain_stuck',
          text: "I'm stuck in my role with no path forward",
          order: 5,
          value: 'STUCK',
        },
      ],
      metadata: {
        maxSelections: 2,
      },
      responseQuotes: [
        {
          id: 'ack_pain_self_advocacy',
          specificResponses: ['pain_credit'],
          headingQuote: "You're not alone",
          quoteText: "82% of professionals say their work isn't noticed enough.",
          author: 'Gallup',
          style: 'info',
        },
        {
          id: 'ack_pain_ai_fluency',
          specificResponses: ['pain_ai_fast'],
          headingQuote: "You're not alone",
          quoteText: '74% worry they are falling behind on AI skills.',
          author: 'Gartner',
          style: 'info',
        },
      ],
    },
    {
      id: 'primary_goal',
      text: 'Pick your primary goal',
      type: 'SINGLE_CHOICE',
      required: true,
      order: 9,
      heading: {
        id: 'goal_heading',
        style: 'heading',
        heading: 'Why are you here?',
      },
      section: {
        id: 'GoalSelectScreen',
        heading: 'Goals',
      },
      options: [
        { id: 'g1', text: 'Grow in my career', value: 'Grow' },
        { id: 'g2', text: 'Become a better leader', value: 'Leader' },
        { id: 'g3', text: 'Unlock better opportunities', value: 'Opportunities' },
        { id: 'g4', text: 'Keep learning and improving', value: 'Learning' },
      ],
      responseQuotes: [
        {
          id: 'ack_goal_testimonial_1',
          allResponses: true,
          headingQuote: "You're in good company",
          quoteText: 'Five helped me speak up and get promoted.',
          author: 'PM @ FAANG',
          style: 'testimonial',
        },
      ],
    },
  ],
  tags: ['five-ai', 'onboarding', 'career'],
};

